<template>
  <page-template :is-loading="isLoading" :data-exists="rateLimitRules !== null">
    <template #pageHeadline>
      {{ $t('rateLimitRule.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="rateLimitRules"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="rate-limit-rules-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewRateLimitRulePath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateRateLimitRule($event as RateLimitRule)"
        @delete-row-button-clicked="openDeleteRateLimitRuleModal($event as RateLimitRule)"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadRateLimitRules"
      />
      <rbz-modal-dialog
        :show="showDeleteRateLimitRuleModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteRateLimitRule"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteRateLimitRuleModal = false"
        @closeModal="showDeleteRateLimitRuleModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedRateLimitRule.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { Action, DocumentName, RateLimitRule } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import {
  shouldUseMockData,
  getRateLimitRulesMockData,
  getSecurityPoliciesMockData,
  getActionsMockData,
} from '@/helpers/MockDataUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'RateLimitRulesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    RbzModalDialog,
    GitHistory,
    PageTemplate,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('rateLimitRule.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getRateLimitRulePath(item);
          },
        },
        {
          id: 'active',
          title: this.$t('rateLimitRule.listPage.active'),
          fieldNames: ['active'],
          displayFunction: (item: RateLimitRule) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-100px',
        },
        {
          id: 'time-frame',
          title: this.$t('rateLimitRule.listPage.timeFrame'),
          fieldNames: ['timeframe'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-110px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'threshold',
          title: this.$t('rateLimitRule.listPage.threshold'),
          fieldNames: ['threshold'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-110px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'action',
          title: this.$t('rateLimitRule.listPage.action'),
          fieldNames: ['action'],
          displayFunction: (item: RateLimitRule) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px action-cell',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'ban',
          title: this.$t('rateLimitRule.listPage.ban'),
          fieldNames: ['is_action_ban'],
          displayFunction: (item: RateLimitRule) => {
            return item['is_action_ban'] ? this.$t('common.yes') : this.$t('common.no');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-90px',
        },
        {
          id: 'event',
          title: this.$t('rateLimitRule.listPage.event'),
          fieldNames: ['pairwith'],
          displayFunction: (item: RateLimitRule) => {
            if (!item.pairwith) {
              return '';
            }
            return _.isEqual(item.pairwith, { self: 'self' })
              ? 'HTTP request'
              : `${Object.keys(item.pairwith)[0]}: ${Object.values(item.pairwith)[0]}`;
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-130px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getRateLimitRulePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      rateLimitRules: null as RateLimitRule[],
      pageLoadingCounter: null as number | null,

      showDeleteRateLimitRuleModal: false,
      selectedRateLimitRule: null as RateLimitRule | null,

      actions: [] as Action[],
      referencedIds: [] as string[],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Rate Limit Rules List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadRateLimitRules();
          await this.loadActions();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('rateLimitRule.plural'),
        singular: this.$t('rateLimitRule.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/rate-limit-rules/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadRateLimitRules() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getRateLimitRulesMockData() };
      } else {
        const url = `conf/${this.selectedBranch}/rate-limit-rules`;
        const fieldNames = _.flatMap(this.columns, 'fieldNames');
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url,
          config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
        });
      }
      this.rateLimitRules = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getSecurityPoliciesMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/security-policies`,
        });
      }
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['rate_limit_rules']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getActionsMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/actions`,
          config: { headers: { 'x-fields': 'id, name' } },
        });
      }
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(rateLimitRule: RateLimitRule) {
      return (
        this.isRateLimitRuleProtected(rateLimitRule) ||
        this.isRateLimitRuleReferenced(rateLimitRule)
      );
    },

    isRateLimitRuleProtected(rateLimitRule: RateLimitRule): boolean {
      return rateLimitRule.id.startsWith('rl-');
    },

    isRateLimitRuleReferenced(rateLimitRule: RateLimitRule): boolean {
      return this.referencedIds.includes(rateLimitRule.id);
    },

    deleteTooltip(rateLimitRule: RateLimitRule): string {
      if (this.isRateLimitRuleProtected(rateLimitRule)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isRateLimitRuleReferenced(rateLimitRule)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getRateLimitRulePath(rateLimitRule: RateLimitRule) {
      return this.getEditPath('rate-limit-rules', rateLimitRule.id);
    },

    getNewRateLimitRulePath() {
      return this.getNewPath('rate-limit-rules');
    },

    editRateLimitRule(rateLimitRule: RateLimitRule) {
      this.navigateTo(this.getRateLimitRulePath(rateLimitRule));
    },

    async duplicateRateLimitRule(rateLimitRule: RateLimitRule) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/rate-limit-rules/${rateLimitRule.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalRateLimitRule = getOriginalResponse.data;
      const rateLimitRuleToAdd = _.cloneDeep(originalRateLimitRule);
      rateLimitRuleToAdd.id = DatasetsUtils.generateUUID2();
      rateLimitRuleToAdd.name = Utils.generateUniqueEntityName(
        rateLimitRuleToAdd.name,
        _.map(this.rateLimitRules, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/rate-limit-rules/${rateLimitRuleToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: rateLimitRuleToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editRateLimitRule(rateLimitRuleToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteRateLimitRule() {
      this.setPageLoadingStatus(true);
      const rateLimitRuleId = this.selectedRateLimitRule.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/rate-limit-rules/${rateLimitRuleId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.rateLimitRules = _.filter(this.rateLimitRules, (rateLimitRule) => {
          return rateLimitRule.id !== rateLimitRuleId;
        });
      }
      this.showDeleteRateLimitRuleModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('rate-limit-rules', 'json', this.rateLimitRules);
    },

    openDeleteRateLimitRuleModal(rateLimitRule: RateLimitRule) {
      this.showDeleteRateLimitRuleModal = true;
      this.selectedRateLimitRule = rateLimitRule;
    },
  },
});
</script>
